import * as THREE from 'three';
import { runWhenIdle } from '../utils.js';

/**
 * LRU cache for streamed terrain tile data.
 *
 *   - Height maps are decoded to raw ImageData (CPU pixel buffer) so the
 *     Terrain class can sample luminance for vertex displacement.
 *   - Colour maps are loaded as THREE.Texture (GPU upload handled by three).
 *   - Each map type has its own LRU (sharing a key namespace is fine but
 *     keeping them separate lets us size them independently later).
 *   - Entries are keyed by `${resolution}/${tileId}` so the same tile at a
 *     different LOD is treated as a distinct entry.
 *
 *  `getHeight` / `getColor` return synchronously:
 *     - cached entry if present (marks it as recently used)
 *     - `undefined` if not yet loaded — and kicks off an async load
 *
 *  Callers poll each frame until the entry becomes available.
 */

export default class TerrainTextureCache {
  constructor({ maxSize = 64, basePath = '', anisotropy = 1 } = {}) {
    this.maxSize = maxSize;
    this.basePath = basePath;
    this.anisotropy = anisotropy;

    /** @type {Map<string, { imageData: ImageData, lastUsed: number }>} */
    this._height = new Map();
    /** @type {Map<string, Promise<void>>} */
    this._heightPending = new Map();

    /** @type {Map<string, { texture: THREE.Texture, lastUsed: number }>} */
    this._color = new Map();
    /** @type {Map<string, Promise<void>>} */
    this._colorPending = new Map();

    this._counter = 0;
    this._loader = new THREE.TextureLoader();
  }

  // ── Height ──────────────────────────────────────────────────────────

  /**
   * @returns {ImageData | undefined}
   */
  getHeight(tileId, res) {
    const key = `${res}/${tileId}`;
    const entry = this._height.get(key);
    if (entry) {
      entry.lastUsed = ++this._counter;
      return entry.imageData;
    }
    this._requestHeight(key, tileId, res);
    return undefined;
  }

  _requestHeight(key, tileId, res) {
    if (this._heightPending.has(key)) return;
    const url = `${this.basePath}terrain/height/${res}/${tileId}.jpg`;
    const p = this._loadImageData(url).then((img) => {
      this._heightPending.delete(key);
      if (img) {
        this._height.set(key, { imageData: img, lastUsed: ++this._counter });
        this._evictImageData(this._height);
      }
    });
    this._heightPending.set(key, p);
  }

  // ── Colour ──────────────────────────────────────────────────────────

  /**
   * @returns {THREE.Texture | undefined}
   */
  getColor(tileId, res) {
    const key = `${res}/${tileId}`;
    const entry = this._color.get(key);
    if (entry) {
      entry.lastUsed = ++this._counter;
      return entry.texture;
    }
    this._requestColor(key, tileId, res);
    return undefined;
  }

  _requestColor(key, tileId, res) {
    if (this._colorPending.has(key)) return;
    const url = `${this.basePath}terrain/color/${res}/${tileId}.jpg`;
    const p = new Promise((resolve) => {
      this._loader.load(
        url,
        (tex) => {
          tex.colorSpace = THREE.SRGBColorSpace;
          tex.wrapS = THREE.ClampToEdgeWrapping;
          tex.wrapT = THREE.ClampToEdgeWrapping;
          tex.anisotropy = this.anisotropy;
          // Heightmap-displaced geometry uses per-vertex position sampling,
          // so the color map needs no special UV treatment.
          this._color.set(key, { texture: tex, lastUsed: ++this._counter });
          this._evictTextures(this._color);
          this._colorPending.delete(key);
          resolve();
        },
        undefined,
        () => {
          console.warn(`TerrainTextureCache: failed to load ${url}`);
          this._colorPending.delete(key);
          resolve();
        },
      );
    });
    this._colorPending.set(key, p);
  }

  // ── Internals ───────────────────────────────────────────────────────

  _loadImageData(url) {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        // Defer the canvas decode (drawImage + getImageData both block the main
        // thread) to idle time so a tile streaming in mid-flight doesn't spike
        // the render frame it lands on.
        runWhenIdle(() => {
          try {
            const canvas = document.createElement('canvas');
            canvas.width = img.naturalWidth;
            canvas.height = img.naturalHeight;
            const ctx = canvas.getContext('2d', { willReadFrequently: true });
            ctx.drawImage(img, 0, 0);
            resolve(ctx.getImageData(0, 0, canvas.width, canvas.height));
          } catch (err) {
            console.warn(`TerrainTextureCache: decode failed for ${url}`, err);
            resolve(null);
          }
        });
      };
      img.onerror = () => {
        console.warn(`TerrainTextureCache: failed to load ${url}`);
        resolve(null);
      };
      img.src = url;
    });
  }

  _evictImageData(map) {
    if (map.size <= this.maxSize) return;
    const sorted = [...map.entries()].sort((a, b) => a[1].lastUsed - b[1].lastUsed);
    const toRemove = map.size - this.maxSize;
    for (let i = 0; i < toRemove; i++) map.delete(sorted[i][0]);
  }

  _evictTextures(map) {
    if (map.size <= this.maxSize) return;
    const sorted = [...map.entries()].sort((a, b) => a[1].lastUsed - b[1].lastUsed);
    const toRemove = map.size - this.maxSize;
    for (let i = 0; i < toRemove; i++) {
      const [k, entry] = sorted[i];
      entry.texture.dispose();
      map.delete(k);
    }
  }

  dispose() {
    for (const { texture } of this._color.values()) texture.dispose();
    this._color.clear();
    this._height.clear();
    this._colorPending.clear();
    this._heightPending.clear();
  }
}
